import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../../services';
import { useUser } from '../../context/UserContext';
import { AdminPageHeader, Card, EmptyLine, Loading } from '../../components/admin/shell';
import api from '../../services/api';
import { API_BASE } from '../../utils/apiBase';
import { SHARE_TEMPLATES } from '../../constants/shareTemplates';
import { webOrigin } from '../../utils/brand';
import StoryCard from '../../components/share/StoryCard';
import ScaledCard from '../../components/share/ScaledCard';
import PosterThumb from '../../components/share/PosterThumb';

const NAMES = {
  p2p_transfers_enabled: ['P2P TRANSFERS', 'Guests can send balance to each other.'],
  golden_tickets_enabled: ['GOLDEN TICKETS', 'Hosts can issue one free ticket per event.'],
  portal_banner_enabled: ['PORTAL BANNER', 'The club banner on the featured wall.'],
  town_square_v2_enabled: ['CITY PORTAL V2', 'The rebuilt city portal page.'],
  party_engine_enabled: ['PARTY ENGINE', 'The concept generator. Off means runs cannot start.'],
  listed_pros_enabled: ['LISTED PROS', 'Directory runs and claim links.'],
  planner_enabled: ['PLANNER', 'The one-prompt party planner for hosts. Off means the worker never runs.'],
  pro_rich_analytics_enabled: ['RICH ANALYTICS FOR PRO PAGES', 'Every Pro page sees the full analytics (who comes back, when they look, regulars, offer by offer, events, sends). Off: only pages paying the Pro subscription see it; the rest see the plan panel.'],
  planner_audience: ['PLANNER · WHO GETS IT', 'Who may open the planner. Admins qualify under every setting but OFF.'],
  'ingestion.enabled': ['EVENT INGESTION', 'The scraper worker. Off means no source is crawled.'],
  'citydiscovery.enabled': ['CITY DISCOVERY', 'Finds a new town’s event sources the first time someone opens its square.'],
  'share_template.event': ['EVENT LINK CARD', 'The card behind every event link.'],
  'share_template.party_starter': ['PARTY STARTER LINK CARD', 'The card behind a Party Starter link.'],
  'share_template.signal': ['PORTAL LINK CARD', 'A Portal’s link, unless its host picked a card.'],
  'share_template.ticket': ['TICKET CARD', 'A shared ticket link.'],
  'share_template.gathering_seat': ['GATHERING SEAT CARD', 'A shared seat link.'],
  'share_template.claim': ['TRANSFER CARD', 'A ticket or seat someone is sending.'],
  'share_template.pro': ['PRO PAGE CARD', 'The card behind every Pro page link.'],
  'share_template.default': ['FALLBACK CARD', 'Any page with no card of its own.'],
  'share_template.wallet': ['WALLET STRIP', 'The strip on an Apple Wallet pass.'],
  'share_template.story': ['STORY IMAGE', 'What the host’s story picker opens on.'],
  'share_template.print': ['PRINT POSTER', 'What the host’s poster picker opens on.'],
  pro_monthly_price_id: ['PRO MONTHLY PRICE', 'Stripe price id for the Pro subscription.'],
};
const SHARE_CARD_KINDS = ['event', 'party_starter', 'signal', 'ticket', 'gathering_seat', 'claim', 'pro', 'default', 'wallet', 'story', 'print'];
const isShareCardKey = (key) => key.startsWith('share_template.');

/**
 * Keys that are a choice, not a switch or a free-text box. A planner run spends real money,
 * so who may start one is picked from a list rather than typed — a typo in a text field would
 * otherwise read as the permissive value.
 */
const CHOICES = {
  ...Object.fromEntries(SHARE_CARD_KINDS.map((k) => [`share_template.${k}`, SHARE_TEMPLATES])),
  planner_audience: [
    ['off', 'OFF'],
    ['admins', 'ADMINS'],
    ['paid', 'PAID PLANS'],
    ['everyone', 'EVERYONE'],
  ],
};

/** Keys the server refuses to write from the panel: shown read-only for everyone. */
const READ_ONLY_KEYS = new Set(['pro_monthly_price_id', 'seo_rewrite_pending_batch_id']); // the server also flags readOnly per row

const kindOf = (value) => (value === 'true' || value === 'false' ? 'switch' : /^-?\d+(\.\d+)?$/.test(value ?? '') ? 'number' : 'text');
const nameOf = (key) => NAMES[key]?.[0] || key.replace(/[._]/g, ' ').toUpperCase();

/** One template of one kind, as it really renders. The sample route is staff-only, so it goes through the API client. */
function ShareThumb({ kind, template, sample }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const fetched = ['ticket', 'gathering_seat', 'claim', 'wallet'].includes(kind);
  useEffect(() => {
    if (!fetched) return undefined;
    let on = true; let url = null;
    api.get(`/admin/share-cards/sample/${kind}.jpg?template=${template}`, { responseType: 'blob' })
      .then((r) => { if (on) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => { if (on) setFailed(true); });
    return () => { on = false; if (url) URL.revokeObjectURL(url); };
  }, [kind, template, fetched]);
  const box = { position: 'relative', display: 'block', width: 240, height: kind === 'story' || kind === 'print' ? 240 : 126, overflow: 'hidden', background: '#111' };
  if (kind === 'story') return <span style={box}><ScaledCard box={240}><StoryCard template={template} words="Your event goes here" topline="YOU’RE " keyword="INVITED" meta="SAT, NOV 7 · 7:05 PM" cta="GET TICKETS →" /></ScaledCard></span>;
  if (kind === 'print') return <span style={box}><PosterThumb template={template} box={240} details={{ title: 'Your event goes here', dateLabel: 'Sat, Nov 7 · 7:05 PM', venue: 'Penticton, BC', url: webOrigin(), code: 'K7Q2PX' }} /></span>;
  if (kind === 'default') return <img alt="" src={`/og-card-${template}.jpg`} style={{ ...box, objectFit: 'cover' }} />;
  if (sample?.card) return <img alt="" src={`${API_BASE}${sample.card}?og=${template}`} style={{ ...box, objectFit: 'cover' }} />;
  if (failed || !fetched) return <span style={box} className="flex items-center justify-center font-mono text-[10px] text-white">NOTHING TO SHOW YET</span>;
  return src ? <img alt="" src={src} style={{ ...box, objectFit: 'cover' }} /> : <span style={box} />;
}

/** The three side by side with a test link each. A test link changes nothing live. */
function ShareRowDetail({ settingKey, samples }) {
  const kind = settingKey.slice('share_template.'.length);
  const [copied, setCopied] = useState(null);
  const path = samples?.[kind]?.page;
  const copy = async (template) => {
    const link = `${webOrigin()}${path}${path.includes('?') ? '&' : '?'}og=${template}`;
    try { await navigator.clipboard.writeText(link); setCopied(template); } catch { setCopied(link); }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-3" data-testid={`share-row-${kind}`}>
      {SHARE_TEMPLATES.map(([key, label]) => (
        <div key={key} className="flex flex-col gap-1.5">
          <ShareThumb kind={kind} template={key} sample={samples?.[kind]} />
          <span className="flex items-center justify-between gap-2 font-mono text-[10px] font-bold tracking-[0.12em]">
            {label}
            {path && <button type="button" onClick={() => copy(key)} className="underline">{copied === key ? 'COPIED' : 'COPY TEST LINK'}</button>}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * SYSTEM › SETTINGS — spec/sections.md §23. Switches, numbers and short text in one list.
 * The four booking-fee keys live under MONEY › BOOKING FEES on their own capability.
 */
export default function SettingsPage() {
  const { can } = useUser();
  const canEdit = can('settings', 'edit');
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState({});
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => { const r = await adminService.getSettings(); setSettings(r.success ? r.data : []); }, []);
  useEffect(() => { load(); }, [load]);
  const [openShare, setOpenShare] = useState(null);
  const [samples, setSamples] = useState(null);
  useEffect(() => {
    if (!openShare || samples) return;
    api.get('/admin/share-cards/samples').then((r) => setSamples(r.data)).catch(() => setSamples({}));
  }, [openShare, samples]);

  const write = async (key, value) => {
    setBusy(key); setErrors((e) => ({ ...e, [key]: '' }));
    const r = await adminService.updateSetting(key, value);
    setBusy(null);
    if (!r.success) { setErrors((e) => ({ ...e, [key]: r.error || 'THIS KEY CANNOT BE CHANGED FROM HERE.' })); return; }
    setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
    load();
  };

  const control = (s) => {
    const kind = kindOf(s.value);
    const editable = canEdit && !READ_ONLY_KEYS.has(s.key) && !s.readOnly;
    const choices = CHOICES[s.key];
    if (choices) {
      const current = (s.value || '').toLowerCase();
      if (!editable) return <span className="font-mono text-[12px] font-bold tracking-[0.05em] text-[#6B7280]">{(choices.find(([v]) => v === current) || [null, current])[1] || '—'}</span>;
      return (
        <span className="flex flex-wrap justify-end gap-[6px]" role="radiogroup" aria-label={nameOf(s.key)} data-testid={`choice-${s.key}`}>
          {choices.map(([value, label]) => {
            const on = value === current;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={busy === s.key}
                onClick={() => { if (!on) write(s.key, value); }}
                className={`h-9 rounded-full px-[14px] font-mono text-[10px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${on ? 'bg-black text-white' : 'bg-[#F2F2F2] text-black hover:bg-[#E5E5E5]'}`}
              >
                {label}
              </button>
            );
          })}
        </span>
      );
    }
    const isCents = /_cents$/.test(s.key);
    const display = kind === 'switch' ? (s.value === 'true' ? 'ON' : 'OFF') : isCents ? `$${(Number(s.value) / 100).toFixed(2)}` : s.value;
    if (!editable) return <span className="font-mono text-[12px] font-bold tracking-[0.05em] text-[#6B7280]">{display || '—'}</span>;
    if (kind === 'switch') {
      const on = s.value === 'true';
      return (
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={nameOf(s.key)}
          disabled={busy === s.key}
          onClick={() => write(s.key, on ? 'false' : 'true')}
          className={`relative h-8 w-14 flex-shrink-0 rounded-full transition-colors duration-200 ${on ? 'bg-black' : 'bg-[#E5E5E5]'} ${busy === s.key ? 'opacity-50' : ''}`}
        >
          <span className="absolute top-1 h-6 w-6 rounded-full bg-white transition-[left] duration-200" style={{ left: on ? 28 : 4 }} />
        </button>
      );
    }
    const draft = drafts[s.key] ?? (isCents ? (Number(s.value) / 100).toFixed(2) : s.value);
    const commit = () => {
      const raw = String(draft).trim();
      const value = isCents ? String(Math.round(parseFloat(raw || '0') * 100)) : raw;
      if (value !== s.value) write(s.key, value);
    };
    return (
      <input
        type={kind === 'number' ? 'number' : 'text'}
        step={isCents ? '0.01' : '1'}
        value={draft}
        onChange={(e) => setDrafts((d) => ({ ...d, [s.key]: e.target.value }))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
        aria-label={nameOf(s.key)}
        className={`h-10 min-w-[110px] rounded-full border-2 border-transparent bg-[#F2F2F2] px-4 font-mono text-[12px] font-bold focus:border-black focus:bg-white focus:outline-none focus:ring-0 ${kind === 'number' ? 'text-right' : 'text-left'}`}
      />
    );
  };

  return (
    <>
      <AdminPageHeader section="settings" meta="Feature switches, numbers and short text, in one list." />
      {!settings ? <Loading /> : (
        <div className="flex flex-col gap-5">
          <Card title="SETTINGS" right="SWITCHES · NUMBERS · TEXT" bodyClass="px-3 pb-3">
            {settings.length === 0 && <EmptyLine />}
            {settings.filter((s) => !isShareCardKey(s.key)).map((s) => (
              <div key={s.key} className="flex items-center gap-4 rounded-[18px] px-3 py-3 transition-colors duration-200 hover:bg-[#F7F7F7]">
                <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                  <div className="font-mono text-[12px] font-bold uppercase tracking-[0.05em]">{nameOf(s.key)}</div>
                  <div className="font-mono text-[11px] leading-[1.5] text-[#6B7280]">{NAMES[s.key]?.[1] || s.description || ''}</div>
                  <div className="font-mono text-[9px] tracking-[0.1em] text-[#9CA3AF]">{s.key}{s.updatedAt ? ` · UPDATED ${new Date(s.updatedAt).toISOString().slice(0, 10)}` : ''}</div>
                  {errors[s.key] && <div className="font-mono text-[11px] text-[#DC2626]">{errors[s.key]}</div>}
                </div>
                <div className="flex-shrink-0">{control(s)}</div>
              </div>
            ))}
          </Card>
          <Card title="SHARE CARDS" right="RING · STREAK · MARQUEE" bodyClass="px-3 pb-3">
            {settings.filter((s) => isShareCardKey(s.key)).map((s) => (
              <div key={s.key} className="rounded-[18px] px-3 py-3">
                <div className="flex flex-wrap items-center gap-4">
                  <button type="button" aria-expanded={openShare === s.key} onClick={() => setOpenShare(openShare === s.key ? null : s.key)} className="flex min-w-0 flex-1 flex-col gap-[3px] text-left">
                    <span className="font-mono text-[12px] font-bold uppercase tracking-[0.05em]">{nameOf(s.key)} {openShare === s.key ? '▴' : '▾'}</span>
                    <span className="font-mono text-[11px] leading-[1.5] text-[#6B7280]">{NAMES[s.key]?.[1]}</span>
                  </button>
                  <div className="flex-shrink-0">{control(s)}</div>
                </div>
                {errors[s.key] && <div className="font-mono text-[11px] text-[#DC2626]">{errors[s.key]}</div>}
                {openShare === s.key && <ShareRowDetail settingKey={s.key} samples={samples} />}
              </div>
            ))}
          </Card>
          <p className="font-mono text-[11px] leading-[1.6] text-[#6B7280]">
            Booking fees are not here. They sit in MONEY → {can('fees', 'view') ? <Link to="/admin/fees" className="underline">BOOKING FEES</Link> : 'BOOKING FEES'} on their own capability, so someone who can flip a feature switch cannot move what the platform charges.
          </p>
        </div>
      )}
    </>
  );
}
